import React from 'react';
import { X, Check, FilmStrip, CreditCard } from '@phosphor-icons/react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PaywallModal = ({ event, onClose, onUpgraded }) => {
  const [loading, setLoading] = React.useState(false);

  const handleUpgrade = async () => {
    setLoading(true);
    try {
      // Create Stripe checkout session for upgrade
      const response = await axios.post(`${API}/create-checkout-session`, {
        event_id: event.event_id,
        package_type: 'premium',
        origin_url: window.location.origin,
        checkout_type: 'upgrade'
      });

      if (response.data.url) {
        // Redirect to Stripe Checkout
        window.location.href = response.data.url;
      } else {
        toast.error('Failed to create checkout session');
      }
    } catch (error) {
      console.error('Upgrade failed:', error);
      toast.error(error.response?.data?.detail || 'Upgrade failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black z-50 flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="paywall-modal"
    >
      <div
        className="relative max-w-2xl w-full bg-black rounded-2xl overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Close Button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 p-2 rounded-full bg-white/10 hover:bg-white/20 text-white transition-colors z-10"
          data-testid="close-paywall"
        >
          <X size={24} weight="bold" />
        </button>

        <div className="p-8 md:p-12">
          {/* Header */}
          <div className="text-center mb-8">
            <div className="inline-flex items-center gap-2 mb-4">
              <FilmStrip size={32} weight="bold" className="text-[#B89762]" />
            </div>
            <h2 className="font-serif text-4xl font-bold text-white mb-4">
              Your Event Film
            </h2>
            <p className="text-gray-400 text-lg">
              Relive your entire event — through your guests' eyes
            </p>
          </div>

          {/* Video Preview Placeholder */}
          <div className="relative rounded-xl overflow-hidden mb-8 h-64 bg-gradient-to-br from-[#15161B] to-[#15161B] flex items-center justify-center">
            <FilmStrip size={64} weight="bold" className="text-white opacity-50" />
          </div>

          {/* Features */}
          <div className="space-y-4 mb-8">
            {[
              'All guest moments combined',
              'Beautifully edited into one story',
              'Background music included',
              'Ready in minutes'
            ].map((feature, i) => (
              <div key={i} className="flex items-center gap-3">
                <div className="flex-shrink-0 w-6 h-6 rounded-full bg-[#B89762] flex items-center justify-center">
                  <Check size={16} weight="bold" className="text-white" />
                </div>
                <p className="text-white text-lg">{feature}</p>
              </div>
            ))}
          </div>

          {/* CTA Button */}
          <button
            onClick={handleUpgrade}
            disabled={loading}
            className="w-full py-4 rounded-xl font-bold text-white text-lg disabled:opacity-50 hover:opacity-90 transition-opacity flex items-center justify-center gap-2"
            style={{
              background: 'linear-gradient(135deg, #B89762 0%, #A033D8 50%, #C29D52 100%)'
            }}
            data-testid="upgrade-cta-button"
          >
            <CreditCard size={22} weight="bold" />
            {loading ? 'Redirecting to payment...' : 'Upgrade to Premium – £15'}
          </button>

          <p className="text-gray-500 text-sm text-center mt-4 flex items-center justify-center gap-1">
            <CreditCard size={14} className="text-gray-500" />
            Secure payment via Stripe
          </p>
        </div>
      </div>
    </div>
  );
};

export default PaywallModal;
